import React, { useEffect, useState } from 'react';
import {
  Container,
  Header,
  CustomerInfo,
  TableContainer,
  Footer
} from './styles';
import {
  IItemSaleInfo,
  ILongSteelShoppingCartState
} from '../../../../../store/ducks/long-steel-shopping-cart/types';
import moment from 'moment';
import {
  CustomersState,
  IPurchaseReceiver,
  ICustomer
} from '../../../../../store/ducks/customers/types';
import { Title } from '@gerdau/hefesto';
import { KeyValueItem, Table } from '@gerdau/hefesto-mais-web';
import { useDispatch, useSelector } from 'react-redux';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { getTotalQuantity } from '../../../../../utils/purchaseHelpers';
import { ApplicationState } from '../../../../../store';

interface ConfirmOrderTableDataSource {
  key: number;
  materialDescription: IMaterialDescription;
  quantity: string;
  unity: string;
  customerPurchaseItem: string;
  date: string;
  destination: string;
  paymentWay: string;
  benefit: string;
}

interface IMaterialDescription {
  material: string;
  code: string;
}

interface IConfirmOrderProps {
  shoppingCartState: ILongSteelShoppingCartState;
  customersState: CustomersState;
  saveOrderInPDF?(element: HTMLElement): void;
}

const ConfirmOrder: React.FC<IConfirmOrderProps> = ({
  shoppingCartState,
  customersState,
  saveOrderInPDF
}) => {
  const dispatch = useDispatch();

  const [confirmOrderData, setConfirmOrderData] = useState<
    ConfirmOrderTableDataSource[]
  >([]);
  const [emitter, setEmitter] = useState<ICustomer>();
  const [receiver, setReceiver] = useState<IPurchaseReceiver>();
  const taggingInfoPreviousScreen = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const updateConfirmOrderData = () => {
    const customerEmitter = customersState.customers.find(
      (cust) => cust.customerId === shoppingCartState.emitterId
    ) as ICustomer;
    const customerReceiver = (
      customerEmitter.purchaseReceivers as IPurchaseReceiver[]
    ).find(
      (rec) => rec.customerId === shoppingCartState.receiverId
    ) as IPurchaseReceiver;

    setEmitter(customerEmitter);
    setReceiver(customerReceiver);

    setConfirmOrderData(
      shoppingCartState.items.map((item, idx) => {
        const itemSaleInfo = item.itemSaleInfo as IItemSaleInfo;
        return {
          key: idx,
          materialDescription: {
            material: item.description,
            code: `Código: ${item.sku}`
          },
          quantity: itemSaleInfo.quantity as string,
          unity: itemSaleInfo.unityMeasure as string,
          customerPurchaseItem: itemSaleInfo.customerPurchaseItem || ' - ',
          date: moment(
            itemSaleInfo.requiredDate as string,
            'YYYY-MM-DD'
          ).format('DD/MM/YYYY'),
          destination: itemSaleInfo.selectedDestination?.description as string,
          paymentWay: itemSaleInfo.selectedDestination?.paymentTerms.find(
            (payment) => payment.term === itemSaleInfo.term
          )?.description as string,
          benefit: itemSaleInfo.benefit || ''
        };
      })
    );
  };

  useEffect(() => {
    if (confirmOrderData.length === 0 && customersState.customers.length > 0) {
      updateConfirmOrderData();

      if (saveOrderInPDF) {
        saveOrderInPDF(
          document.getElementById('confirm-order-container-pdf') as HTMLElement
        );
      }
    }
  });

  useEffect(() => {
    dispatch(
      sendTagging({
        page: document.location.pathname,
        screenName: `LG:Comprar:AcosLongos:${taggingInfoPreviousScreen.screenName}:RevisarPedido`,
        event: 'page_view'
      })
    );
  }, [dispatch]);

  const getColumns = () => {
    const columns = [
      {
        title: 'Item do pedido',
        dataIndex: 'customerPurchaseItem',
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Descrição do Material',
        dataIndex: 'materialDescription',
        render: (materialDescription: IMaterialDescription) => (
          <>
            {materialDescription.material}
            <br></br>
            {materialDescription.code}
          </>
        )
      },
      {
        title: 'Quantidade',
        dataIndex: 'quantity',
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Unidade',
        dataIndex: 'unity',
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Data desejada',
        dataIndex: 'date',
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Destinação',
        dataIndex: 'destination',
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Forma de pagamento',
        dataIndex: 'paymentWay',
        width: '9rem',
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Benefício fiscal',
        dataIndex: 'benefit',
        width: '8rem',
        render: (text: string) => (
          <>{text !== '' ? text : 'Não utilizar benefício'}</>
        )
      }
    ];

    return columns;
  };

  return (
    <Container id={`${saveOrderInPDF ? 'confirm-order-container-pdf' : ''}`}>
      <div className="header-container">
        <Header>
          {shoppingCartState.customerOrder !== '' ? (
            <Title
              level={2}
            >{`Número do pedido: ${shoppingCartState.customerOrder}`}</Title>
          ) : (
            <Title level={2}>Ordem de venda</Title>
          )}
        </Header>
      </div>
      <CustomerInfo className={`${saveOrderInPDF ? 'pdf' : ''}`}>
        <div>
          <KeyValueItem title={`Emissor`}>
            {emitter?.name} - {emitter?.locality.address}
            <br></br>
            CNPJ: {emitter?.taxNumber}
          </KeyValueItem>
        </div>

        <div>
          <KeyValueItem title={`Recebedor`}>
            <span>
              {receiver?.name} - {receiver?.address}
              <br></br>
              CNPJ: {receiver?.taxNumber}
            </span>
          </KeyValueItem>
        </div>
      </CustomerInfo>
      <TableContainer>
        <Table
          columns={getColumns()}
          dataSource={confirmOrderData}
          pagination={false}
        />
      </TableContainer>
      {saveOrderInPDF && (
        <Footer>
          <div className="quantites">
            <div>
              <span>Quantidade de itens </span>
              <strong>{shoppingCartState.items.length}</strong>
            </div>
            <div>
              <span>Quantidade total </span>
              <strong>{getTotalQuantity(shoppingCartState.items)} t</strong>
            </div>
          </div>
        </Footer>
      )}
    </Container>
  );
};

export default ConfirmOrder;
